import React from 'react';
import { FaHtml5, FaCss3Alt, FaJs, FaNodeJs, FaGitAlt } from 'react-icons/fa';
import { SiTypescript, SiReact, SiFramer, SiPostgresql, SiMongodb, SiNextdotjs, SiAdonisjs, SiSolidity, SiTailwindcss, SiPython } from 'react-icons/si';
import veeKite from '../Image/veekite.png';
import Confess from '../Image/confess.png';
import BlockchainExplorer from '../Image/blockchain-explorer.jpg';
import Vortexis from '../Image/vortexis.jpg';

// Grouped Tech Skills
export const techGroups = [
  {
    category: "Frontend",
    skills: [
      { name: 'HTML', icon: <FaHtml5 className="text-[#e34f26]" size={28} /> },
      { name: 'CSS', icon: <FaCss3Alt className="text-[#1572b6]" size={28} /> },
      { name: 'JavaScript', icon: <FaJs className="text-[#f7df1e]" size={28} /> },
      { name: 'TypeScript', icon: <SiTypescript className="text-[#3178c6]" size={28} /> },
      { name: 'React', icon: <SiReact className="text-[#61dafb]" size={28} /> },
      { name: 'Next.js', icon: <SiNextdotjs className="text-black dark:text-white" size={28} /> },
      { name: 'Tailwind CSS', icon: <SiTailwindcss className="text-[#38bdf8]" size={28} /> },
    ]
  },
  {
    category: "Backend",
    skills: [
      { name: 'Node.js', icon: <FaNodeJs className="text-[#339933]" size={28} /> },
      { name: 'AdonisJS', icon: <SiAdonisjs className="text-[#5a45ff]" size={28} /> },
      { name: 'Python', icon: <SiPython className="text-[#3776ab]" size={28} /> },
    ]
  },
  {
    category: "Database",
    skills: [
      { name: 'PostgreSQL', icon: <SiPostgresql className="text-[#336791]" size={28} /> },
      { name: 'MongoDB', icon: <SiMongodb className="text-[#47a248]" size={28} /> },
    ]
  },
  {
    category: "Web3",
    skills: [
      { name: 'Solidity', icon: <SiSolidity className="text-[#363636] dark:text-[#8c8c8c]" size={28} /> },
    ]
  },
  {
    category: "Tools & Extras",
    skills: [
      { name: 'Git', icon: <FaGitAlt className="text-[#f05032]" size={28} /> },
      { name: 'Framer Motion', icon: <SiFramer className="text-[#f0047f]" size={28} /> },
    ]
  }
];

export const Experience = [
  {
    role: "Software Developer",
    company: "Web3bridge",
    time: "Dec 2025 - current",
    responsibilities: [
      "Designing and implementing frontend, backend, and smart contract functionalities for decentralized applications.",
      "Leveraging Python and other modern backend technologies to streamline automation and enhance developers' experience.",
      "Writing clean, well-tested, and secure code, participating in peer reviews, and aligning with team quality standards.",
      "Improving platform capabilities by integrating internal and external APIs and services."
    ]
  },
  {
    role: "Frontend Engineer",
    company: "SphiderAssWeb",
    time: "Feb 2025 - Mar 2026",
    responsibilities: [
      "Developing and maintaining web applications using modern frameworks and technologies.",
      "Collaborating with cross-functional teams to create user-friendly and efficient applications.",
      "Integrating RESTful APIs for seamless communication between frontend and backend services.",
      "Conducting code reviews, implementing testing strategies, and ensuring high-quality code standards.",
      "Ensuring applications are responsive and perform well across a variety of devices and screen sizes.",
      "Using version control systems (like Git) for tracking changes and collaborating on code.",
      "Analyzing and improving application performance, addressing bottlenecks and enhancing user experience.",
      "Continuously researching and implementing emerging technologies and best practices in web development."
    ]
  },
  {
    role: "Fullstack Web & Mobile Developer",
    company: "Ullweb",
    time: "June 2024 - June 2025",
    responsibilities: [
      "Developing and maintaining web and mobile applications using modern frameworks and technologies.",
      "Collaborating with cross-functional teams to create user-friendly and efficient applications.",
      "Creating and integrating RESTful APIs for seamless communication between frontend and backend services.",
      "Designing and managing databases, ensuring data integrity and security, and optimizing query performance.",
      "Ensuring applications are responsive and perform well across a variety of devices and screen sizes.",
      "Continuously researching and implementing emerging technologies and best practices in web and mobile development.",
      "Identifying and fixing bugs, performance issues, and other technical challenges."
    ]
  },
  {
    role: "Frontend Developer Intern",
    company: "Web3bridge",
    time: "June 2024 - Dec 2024",
    responsibilities: [
      "Assisted in developing responsive web applications using Javascript, Next.js and Typescript.",
      "Collaborated with designers to implement user-friendly interfaces and improve UX.",
      "Participated in code reviews and contributed to team knowledge sharing.",
      "Maintained and updated existing applications to enhance performance and usability.",
      "Learned and utilized frameworks like React to build dynamic web components.",
      "Collaborated with backend developers and implemented the created APIs to the frontend."
    ]
  }
];

export const Portfolios = [
  {
    image: Vortexis,
    name: "Vortexis",
    tagline: "core contributor / frontend dev",
    desc: "An all-in-one hackathon hosting and collaboration platform designed to simplify team formation, project submissions, and judge evaluations.",
    features: [
      "End-to-end hackathon hosting, registration, and management workflow",
      "Real-time analytics dashboard on participation and project engagement",
      "Integrated workspaces for team formation and project collaboration",
      "Seamless submission tracking and custom evaluation rubrics for judges"
    ],
    tech: ["Next.js", "TypeScript", "Tailwind CSS", "Framer Motion"],
    link: "https://vortexis.web3bridgegarage.com/",
    featured: false
  },
  {
    image: Confess,
    name: "Confess",
    tagline: "flagship product",
    desc: "A platform that allows users to send anonymous messages, voice notes, and confessions to express feelings without revealing identity.",
    features: [
      "Anonymous messaging with custom templates",
      "Personalized celebration experiences and animated event cards",
      "Voice-note based confessions with custom wave rendering",
      "Voice transformation and privacy filters for secure speaking",
      "Robust admin and campaign analytics dashboard"
    ],
    tech: ["Next.js", "TypeScript", "Node.js", "WebRTC", "PostgreSQL", "Tailwind CSS"],
    link: "https://confess.com.ng/",
    featured: true
  },
  {
    image: BlockchainExplorer,
    name: "Tenski Scan",
    tagline: "contributor / explorer",
    desc: "A high-precision developer-focused blockchain indexer and transaction explorer. I contributed to the frontend dashboard, gas fee metrics, and real-time transaction updates.",
    features: [
      "Real-time block validation and gas fee tracker feed",
      "Comprehensive search by Address, Transaction Hash, or Block height",
      "Support for Ethereum Mainnet, Sepolia Testnet, and Holesky Testnet"
    ],
    tech: ["Next.js", "TypeScript", "Tailwind CSS", "Ethers.js", "Recharts"],
    link: "https://block-chain-explorer-five.vercel.app/",
    featured: false
  },
  {
    image: veeKite,
    name: "Veekite",
    tagline: "corporate engineering",
    desc: "A modern corporate portal built for Veekite, an engineering company based in Nigeria, showcasing their projects and consulting expertise.",
    features: [
      "Interactive 3D project gallery and interactive service showcases",
      "Lead generation forms and request-for-proposal portal"
    ],
    tech: ["React", "Framer Motion", "Tailwind CSS"],
    link: "https://www.veekites.com/",
    featured: false
  }
];

export const OpenSourcePackages = [
  {
    name: "progressive-shapes",
    desc: "A lightweight UI utility/library for creating progressive shape-based visual effects and interfaces.",
    repo: "https://github.com/feranmiba/progressive-shapes",
    npmLink: "https://www.npmjs.com/package/progressive-shapes",
    installCmd: "npm i progressive-shapes",
    tech: ["React", "TypeScript", "SVG Animation", "Tailwind CSS"],
    status: "published"
  },
  {
    name: "tiny-editor",
    desc: "A lightweight text editor package designed to make it easy to add text editing functionality to web applications.",
    repo: "https://github.com/feranmiba/tiny-editor",
    npmLink: "https://www.npmjs.com/package/light-text-editor", 
    installCmd: "npm i light-text-editor",
    tech: ["Typescript", "HTML5", "CSS3", "Rich Text"],
    status: "published"
  }
];
